import { cn } from '@utazzo/ui';
import { ChevronRight, CornerDownRight } from 'lucide-react';
import React, { useState } from 'react';

import {
  GRAND_TOTAL_ROW,
  GRID_TABLE,
  numberTone,
  REPORT_HEAD_CELL,
  useGroupOpen,
} from './month-table';

import type { ContactabilityRow, MarketingContactabilityResponse } from '@utazzo/shared';

import { ReportSection } from '@/features/reports/components/ReportSection';

const count = (value: number): string => value.toLocaleString('en-IN');
const pct = (value: number | null): string => (value === null ? '-' : `${value.toFixed(0)}%`);

/** Header band shared by every table in the report. */
const HEAD = REPORT_HEAD_CELL;

/** Shared header: Name | Querys | Active | Not Qualified | Lost | Contacted %. */
const Head = ({ label }: { label: string }): React.JSX.Element => (
  <thead>
    <tr>
      <th className={cn(HEAD, 'text-left')}>{label}</th>
      <th className={cn(HEAD, 'text-right')}>Querys</th>
      <th className={cn(HEAD, 'text-right')}>Active</th>
      <th className={cn(HEAD, 'text-right')}>Not Qualified</th>
      <th className={cn(HEAD, 'text-right')}>Lost</th>
      <th className={cn(HEAD, 'text-right')}>Contacted %</th>
    </tr>
  </thead>
);

/** Querys | Active | Not Qualified | Lost | Contacted %, each coloured by meaning. */
const Cells = ({ row, muted }: { row: ContactabilityRow; muted?: boolean }): React.JSX.Element => {
  const active = row.active ?? 0;
  const notQualified = row.notQualified ?? 0;
  const columns = [
    { text: count(row.queries), tone: numberTone(row.queries) },
    { text: count(active), tone: numberTone(active, 'good') },
    { text: count(notQualified), tone: numberTone(notQualified, 'bad') },
    { text: count(row.lost), tone: numberTone(row.lost, 'bad') },
    { text: pct(row.contactedPct), tone: numberTone(row.contactedPct, 'good') },
  ];
  return (
    <>
      {columns.map((column, index) => (
        <td
          key={index}
          className={cn(
            'px-3 py-1.5 text-right whitespace-nowrap tabular-nums',
            muted ? 'text-muted-foreground' : column.tone,
          )}
        >
          {column.text}
        </td>
      ))}
    </>
  );
};

const TotalRow = ({ row }: { row: ContactabilityRow }): React.JSX.Element => (
  <tr className={GRAND_TOTAL_ROW}>
    <th scope="row" className="text-foreground px-3 py-2 text-left whitespace-nowrap">
      {row.name}
    </th>
    <Cells row={row} />
  </tr>
);

const skeleton = (
  <div className="space-y-2 py-2">
    {[0, 1, 2].map((index) => (
      <div key={index} className="bg-muted h-8 animate-pulse rounded" />
    ))}
  </div>
);

/**
 * "Region wise contactibility" and "Agent Wise Contactibility".
 *
 * Both cover the whole selected range as a single period, matching the source
 * sheet. Contacted % is leads with a first-contact timestamp over queries.
 *
 * The agent table groups by lead SOURCE with agents nested, so offline sources
 * (walk-in, referral, tele-calling) sit in their own groups rather than being
 * mixed into the paid channels.
 */
export const ContactabilityTables = ({
  data,
  loading,
}: {
  data: MarketingContactabilityResponse | undefined;
  loading: boolean;
}): React.JSX.Element => {
  const groupOpen = useGroupOpen();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (key: string): void =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  /** The two grand totals can differ - explain it rather than hide it. */
  const totalsDiffer = data !== undefined && data.regionTotal.queries !== data.channelTotal.queries;

  return (
    <>
      <ReportSection title="Region wise contactibility">
        {loading || !data ? (
          skeleton
        ) : (
          <table className={cn('w-full min-w-md text-[15px]', GRID_TABLE)}>
            <Head label="Name" />
            <tbody>
              {data.regions.map((region) => (
                <tr key={region.key}>
                  <th
                    scope="row"
                    className="text-foreground px-3 py-1.5 text-left font-normal whitespace-nowrap"
                  >
                    {region.name}
                  </th>
                  <Cells row={region} />
                </tr>
              ))}
              <TotalRow row={data.regionTotal} />
            </tbody>
          </table>
        )}
        {!loading && data && (
          <p className="text-muted-foreground mt-3 text-xs">
            Region comes from the lead&apos;s departure city (the working field first, then the
            captured intake origin, then free-text). &ldquo;Delhi NCR&rdquo; is the ~150km Delhi
            servicing catchment; everything else - including leads with no departure city - is
            &ldquo;Non-Delhi NCR&rdquo;.
          </p>
        )}
      </ReportSection>

      <ReportSection title="Agent Wise Contactibility">
        {loading || !data ? (
          skeleton
        ) : (
          <>
            <table className={cn('w-full min-w-md text-[15px]', GRID_TABLE)}>
              <Head label="Name" />
              <tbody>
                {data.channels.map((channel) => {
                  const open = groupOpen(expanded.has(channel.key));
                  return (
                    <React.Fragment key={channel.key}>
                      <tr className="bg-muted/40 font-semibold">
                        <th
                          scope="row"
                          className="text-foreground px-3 py-1.5 text-left whitespace-nowrap"
                        >
                          <button
                            type="button"
                            onClick={() => toggle(channel.key)}
                            title={open ? 'Hide agents' : 'Show agents'}
                            className="flex items-center gap-1.5"
                          >
                            <ChevronRight
                              className={cn(
                                'text-muted-foreground h-3.5 w-3.5 shrink-0 transition-transform',
                                open && 'rotate-90',
                              )}
                            />
                            {channel.name}
                            <span className="bg-primary/10 text-primary rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums">
                              {channel.agents.length}
                            </span>
                          </button>
                        </th>
                        <Cells row={channel} />
                      </tr>
                      {open &&
                        channel.agents.map((agent) => (
                          <tr key={`${channel.key}-${agent.key}`}>
                            <th
                              scope="row"
                              className="text-muted-foreground px-3 py-1.5 text-left font-normal whitespace-nowrap"
                            >
                              <span className="flex items-center gap-1.5 pl-5">
                                <CornerDownRight className="h-3.5 w-3.5 shrink-0" />
                                {agent.name}
                              </span>
                            </th>
                            <Cells row={agent} muted />
                          </tr>
                        ))}
                    </React.Fragment>
                  );
                })}
                <TotalRow row={data.channelTotal} />
              </tbody>
            </table>
            {totalsDiffer && (
              <p className="text-muted-foreground mt-3 text-xs">
                This total ({count(data.channelTotal.queries)}) differs from the region table (
                {count(data.regionTotal.queries)}). The two count the same leads, so any gap is
                leads sitting in an &ldquo;Unassigned&rdquo; agent row.
              </p>
            )}
          </>
        )}
      </ReportSection>
    </>
  );
};
